import { ReactElement, useState, useEffect } from 'react';
import { Box } from '@twilio-paste/core/box';
import { Button } from '@twilio-paste/core/button';
import { Grid, Column } from '@twilio-paste/core/grid';
import { Stack } from '@twilio-paste/core/stack';
import { ChevronDownIcon } from '@twilio-paste/icons/esm/ChevronDownIcon';
import { ChevronUpIcon } from '@twilio-paste/icons/esm/ChevronUpIcon';
import EmptyState, {
  emptyStateTypes,
} from '@twiliointernal/services-common/components/emptyState';
import RouterAnchor from '@twiliointernal/services-common/components/routerAnchor';

import ProductCardLoaders from '../../productCardLoaders';
import ProductCard from '../../productCard';
import { getProductIconNameByZendeskCategoryId } from '../../productCard/utils';

import { useFetchArticlesByProducts, useSegment } from '../../../hooks';

import { trackingDetails } from '../../../definitions/constants';
import { copy, constants } from '../definitions';
import { types as productCardTypes } from '../../productCard/definitions';
import logger from '../../../../lib/logger';

import styles from '../landing.module.css';

const ArticlesByProduct = (): ReactElement => {
  const { trackEvent } = useSegment();
  const [isViewingMore, setIsViewingMore] = useState<boolean>(false);
  const { data, isLoading, error } = useFetchArticlesByProducts();
  const hasError = error || (Array.isArray(data) && !data.length);
  const articlesByProduct = data?.categories || [];

  const productCardsData = isViewingMore
    ? articlesByProduct
    : articlesByProduct?.slice(0, 6);

  const handleViewMoreButtonClick = (): void => {
    setIsViewingMore((prevState) => !prevState);
    const eventName = trackingDetails.categoriesViewMore;

    logger.info(`${constants.LOG_PREFIX}::[${eventName}]`);
    trackEvent(eventName);
  };

  const handleArticleAnchorClick = (id, title): void => {
    const eventName = trackingDetails.categoryArticleLinkClicked;

    logger.info(`${constants.LOG_PREFIX}::[${eventName}]`);
    trackEvent(eventName, {
      id,
      title,
    });
  };

  const handleArticleViewMoreClick = (id, name): void => {
    const eventName = trackingDetails.categoryViewMoreClicked;

    logger.info(`${constants.LOG_PREFIX}::[${eventName}]`);
    trackEvent(eventName, {
      id,
      name,
    });
  };

  useEffect(() => {
    if (hasError) {
      logger.error(`${constants.LOG_PREFIX}::${copy.errors.log}`);
    }
  }, [hasError]);

  if (isLoading) {
    return (
      <ProductCardLoaders
        cardContainerClassName={styles.productCardContainer}
        icon={{
          name: productCardTypes.ProductIconName.Twilio,
        }}
      />
    );
  }

  if (hasError) {
    return (
      <Box className={styles.productCardErrorContainer}>
        <EmptyState
          heading={copy.emptyState.oops.heading}
          description={copy.emptyState.oops.description}
          variant={emptyStateTypes.EmptyStateVariant.oops}
          maxWidth="49rem"
        />
      </Box>
    );
  }

  return (
    <>
      <Grid gutter="space50" vertical={false} equalColumnHeights>
        {productCardsData.map(({ articles, name, id: categoryId }) => {
          const articlesCount = articles.length;
          const articlesToRender = articles.slice(0, 3);
          const iconName = getProductIconNameByZendeskCategoryId(categoryId);
          const showViewMore = articlesCount ? articlesCount > 3 : false;
          const viewMoreHref = `/categories/${categoryId}`;

          return (
            <Column key={categoryId} span={[12, 6, 4]}>
              <Box className={styles.productCardContainer}>
                <ProductCard
                  articlesCount={articlesCount}
                  heading={name}
                  height={['280px', '330px', '280px']}
                  icon={{
                    color: 'decorative10',
                    name: iconName,
                  }}
                  viewMoreOptions={{
                    href: viewMoreHref,
                    isVisible: showViewMore,
                    trackEvent: () =>
                      handleArticleViewMoreClick(categoryId, name),
                  }}
                >
                  <Stack orientation="vertical" spacing="space30">
                    {articlesToRender.map(({ id, title }) => (
                      <RouterAnchor
                        key={id}
                        to={`/articles/${id}`}
                        onClick={(): void =>
                          handleArticleAnchorClick(id, title)
                        }
                      >
                        {title}
                      </RouterAnchor>
                    ))}
                  </Stack>
                </ProductCard>
              </Box>
            </Column>
          );
        })}
      </Grid>

      <Box textAlign="center" marginY="space70">
        <Button variant="primary_icon" onClick={handleViewMoreButtonClick}>
          {copy.browseArticlesByProduct.button.viewMoreArticles(isViewingMore)}
          {isViewingMore ? (
            <ChevronUpIcon decorative />
          ) : (
            <ChevronDownIcon decorative />
          )}
        </Button>
      </Box>
    </>
  );
};

export default ArticlesByProduct;
